import React, { useEffect, useMemo, useState } from 'react';
import { api, downloadApiFile, uploadWithFormData } from '../api';
import { useBankConfig } from '../bank-config';

const SEARCH_COLUMN_CONFIG = [
  { key: 'fio', label: 'ФИО', render: (item) => item.fio || '-' },
  { key: 'phone', label: 'Телефон', render: (item) => item.phone || '-' },
  { key: 'company', label: 'Компания', render: (item) => item.company_name || '-' },
  { key: 'email', label: 'Email', render: (item) => item.email || '-' },
  { key: 'inn', label: 'ИНН', render: (item) => item.inn || '-' },
  { key: 'ogrn', label: 'ОГРН', render: (item) => item.ogrn || '-' },
  { key: 'city', label: 'Город', render: (item) => item.city || '-' }
];

function isFieldEnabled(fileStructure, key) {
  const field = fileStructure?.[key];
  return field?.enabled !== false;
}


function decodeTokenPermissions() {
  try {
    const token = localStorage.getItem('callnote_token');
    if (!token) return [];
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Array.isArray(payload.permissions) ? payload.permissions : [];
  } catch (_error) {
    return [];
  }
}

export function SearchPage() {
  const bankConfig = useBankConfig();
  const { isTochka } = bankConfig;
  const [applicationMessage, setApplicationMessage] = useState('');
  const [searchField, setSearchField] = useState('phone');
  const [searchValue, setSearchValue] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [fileSearch, setFileSearch] = useState({ column: 1, hasHeader: true, file: null });
  const [dragActive, setDragActive] = useState(false);
  const [rowInputs, setRowInputs] = useState({});
  const [submittingIds, setSubmittingIds] = useState({});
  const [fileStructure, setFileStructure] = useState({});

  const permissions = useMemo(() => decodeTokenPermissions(), []);
  const canApply = permissions.includes('*') || permissions.includes('files.manage');
  const isInnSearch = searchField === 'inn';
  const busy = loading || exporting;

  function changeSearchField(field) {
    if (busy || field === searchField) return;
    setSearchField(field);
    setSearchValue('');
    setRows([]);
    setError('');
    setRowInputs({});
    setFileSearch((prev) => ({ ...prev, file: null }));
    setDragActive(false);
  }

  useEffect(() => {
    if (!canApply) return;
    api('/settings')
      .then((data) => setFileStructure(data?.file_structure || {}))
      .catch(() => {});
  }, [canApply]);

  const visibleColumns = useMemo(() => SEARCH_COLUMN_CONFIG.filter((column) => isFieldEnabled(fileStructure, column.key)), [fileStructure]);

  useEffect(() => {
    if (!rows.length) return;
    setRowInputs((prev) => {
      const next = { ...prev };
      for (const row of rows) {
        if (!next[row.id]) {
          next[row.id] = { productCode: '', advCode: '' };
        }
      }
      return next;
    });
  }, [rows]);

  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }

  function handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0] || null;
    if (droppedFile) {
      setFileSearch((prev) => ({ ...prev, file: droppedFile }));
    }
  }

  async function search() {
    if (!searchValue.trim()) {
      setError(isInnSearch ? 'Введите ИНН для поиска' : 'Введите телефон для поиска');
      return;
    }
    if (isInnSearch && !/^(?:\d{10}|\d{12})$/.test(searchValue.replace(/\D/g, ''))) {
      setError('Введите полный ИНН: 10 или 12 цифр');
      return;
    }
    setLoading(true);
    setError('');
    setRows([]);
    try {
      setRows(await api(`/search/contacts?field=${searchField}&${searchField}=${encodeURIComponent(searchValue)}`));
    } finally {
      setLoading(false);
    }
  }

  async function searchByFile() {
    if (!fileSearch.file) {
      setError('Выберите файл для поиска');
      return;
    }
    setLoading(true);
    setError('');
    setRows([]);
    try {
      const fd = new FormData();
      fd.append('field', searchField);
      fd.append('column', String(fileSearch.column || 1));
      fd.append('hasHeader', fileSearch.hasHeader ? 'true' : 'false');
      fd.append('file', fileSearch.file);
      const result = await uploadWithFormData('/search/contacts/by-file', fd, { timeoutMs: 120000 });
      setRows(Array.isArray(result) ? result : []);
      setFileSearch((prev) => ({ ...prev, file: null }));
    } finally {
      setLoading(false);
      setDragActive(false);
    }
  }

  function updateRowInput(id, patch) {
    setRowInputs((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || { productCode: '', advCode: '' }),
        ...patch
      }
    }));
  }

  async function submitApplication(id) {
    const form = rowInputs[id] || {};
    if (bankConfig.loading || bankConfig.error || (!isTochka && (!form.productCode || !form.advCode))) return;

    setSubmittingIds((prev) => ({ ...prev, [id]: true }));
    setError('');
    setApplicationMessage('');
    try {
      const result = await api(`/search/contacts/${id}/application`, {
        method: 'POST',
        body: JSON.stringify({
          productCode: form.productCode,
          advCode: form.advCode
        })
      });
      if (isTochka && result?.dryRun) setApplicationMessage('Тестовый режим: данные заявки проверены, в Точку ничего не отправлено.');
      else setRows((prev) => prev.map((item) => item.id === id ? { ...item, status: 'bank_application_sent' } : item));
    } finally {
      setSubmittingIds((prev) => ({ ...prev, [id]: false }));
    }
  }

  async function exportResults() {
    setExporting(true);
    setError('');
    try {
      await downloadApiFile('/search/export', 'search_results.xls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactIds: rows.map((item) => item.id) }),
        timeoutMs: 120000
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="grid two-columns">
      {error ? <section className="card full-width error-banner">{error}</section> : null}
      {bankConfig.error ? <section className="card full-width error-banner">{bankConfig.error}</section> : null}
      {applicationMessage ? <section className="card full-width success-banner">{applicationMessage}</section> : null}

      <section className="card full-width">
        <div className="inline-actions" role="group" aria-label="Способ поиска">
          <button type="button" className={`tab-button${!isInnSearch ? ' active' : ''}`} aria-pressed={!isInnSearch} disabled={busy} onClick={() => changeSearchField('phone')}>
            По телефону
          </button>
          <button type="button" className={`tab-button${isInnSearch ? ' active' : ''}`} aria-pressed={isInnSearch} disabled={busy} onClick={() => changeSearchField('inn')}>
            По ИНН
          </button>
        </div>
      </section>

      <section className="card">
        <h2>{isInnSearch ? 'Поиск по ИНН' : 'Поиск по телефону'}</h2>
        <form onSubmit={(event) => {
          event.preventDefault();
          if (!busy) search().catch((currentError) => setError(currentError.message || 'Не удалось выполнить поиск'));
        }}>
          <label>
            {isInnSearch ? 'ИНН' : 'Телефон'}
            <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} inputMode={isInnSearch ? 'numeric' : 'tel'} placeholder={isInnSearch ? '10 или 12 цифр' : '79999999999'} disabled={busy} />
          </label>
          <button type="submit" disabled={busy}>
            {loading ? 'Поиск...' : 'Найти'}
          </button>
        </form>
      </section>

      <section className="card">
        <h2>Поиск по файлу</h2>
        <label>
          {isInnSearch ? 'Колонка ИНН' : 'Колонка телефона'}
          <input
            type="number"
            className="blocklist-number"
            min="1"
            value={fileSearch.column}
            onChange={(e) => setFileSearch((prev) => ({ ...prev, column: Number(e.target.value) || 1 }))}
          />
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={fileSearch.hasHeader}
            onChange={(e) => setFileSearch((prev) => ({ ...prev, hasHeader: e.target.checked }))}
          />
          Есть строка заголовков
        </label>

        <label
          className={`dropzone${dragActive ? ' active' : ''}${fileSearch.file ? ' has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            key={fileSearch.file ? fileSearch.file.name : 'empty'}
            type="file"
            className="dropzone-input"
            onChange={(e) => setFileSearch((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
          />
          <div className="dropzone-title">{fileSearch.file ? 'Файл выбран' : 'Перетащите файл сюда'}</div>
          <div className="dropzone-hint">{fileSearch.file ? fileSearch.file.name : 'или нажмите, чтобы выбрать файл'}</div>
        </label>

        <button
          type="button"
          className="blocklist-import_file-button"
          disabled={loading || exporting || !fileSearch.file}
          onClick={() => searchByFile().catch((currentError) => setError(currentError.message || 'Не удалось выполнить поиск по файлу'))}
        >
          {loading ? 'Поиск...' : 'Поиск'}
        </button>
      </section>

      <section className="card full-width">
        <div className="section-header">
          <h2>Результаты поиска</h2>
        </div>

        {loading ? <div className="muted">Поиск...</div> : null}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                {visibleColumns.map((column) => <th key={column.key}>{column.label}</th>)}
                <th>Статус</th>
                <th>Результат звонка</th>
                {canApply ? <>{!isTochka ? <><th>Product code</th><th>Adv Code</th></> : null}<th></th></> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                const currentInputs = rowInputs[item.id] || { productCode: '', advCode: '' };
                const applicationDisabled = bankConfig.loading || Boolean(bankConfig.error) || (!isTochka && (!currentInputs.productCode || !currentInputs.advCode)) || Boolean(submittingIds[item.id]);
                return (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    {visibleColumns.map((column) => (
                      <td key={`${item.id}-${column.key}`}>{column.render(item)}</td>
                    ))}
                    <td><span className={`status-chip status-${item.status}`}>{item.status || '-'}</span></td>
                    <td>{item.call_result_name || '-'}</td>
                    {canApply ? (
                      <>
                      {!isTochka ? <>
                      <td>
                        <input
                          value={currentInputs.productCode || ''}
                          onChange={(e) => updateRowInput(item.id, { productCode: e.target.value })}
                          placeholder="LP_RKO"
                        />
                      </td>
                      <td>
                        <input
                          value={currentInputs.advCode || ''}
                          onChange={(e) => updateRowInput(item.id, { advCode: e.target.value })}
                          placeholder="advCode"
                        />
                      </td>
                      </> : null}
                      <td>
                        <button
                          type="button"
                          disabled={applicationDisabled}
                          onClick={() => submitApplication(item.id).catch((currentError) => setError(currentError.message || 'Не удалось отправить заявку'))}
                        >
                          {submittingIds[item.id] ? 'Отправка...' : 'Заявка'}
                        </button>
                      </td>
                      </>
                    ) : null}
                  </tr>
                );
              })}
              {!rows.length && !loading ? <tr><td colSpan={canApply ? (visibleColumns.length + (isTochka ? 4 : 6)) : (visibleColumns.length + 3)}>Ничего не найдено</td></tr> : null}
            </tbody>
          </table>
        </div>

        {rows.length ? (
          <div className="inline-actions top-gap">
            <button type="button" disabled={loading || exporting} onClick={() => exportResults().catch((currentError) => setError(currentError.message || 'Не удалось выгрузить Excel'))}>
              {exporting ? 'Формирование...' : 'Excel'}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
